/**
 * Bake-at-save event binding for website-builder nodes.
 *
 * A bound node carries `eventBindings: { [propName]: EventBindingKey }`. The
 * editor resolves each key against the event and writes the CONCRETE value
 * into the prop — the serialized page JSON that reaches the visitor runtime
 * contains plain values, so the runtime needs no binding knowledge. Values
 * refresh whenever the editor loads (refreshBoundNodes) and on toggle.
 *
 * Deliberate limitation: values go stale until an admin re-opens the builder
 * and saves — acceptable v1; a staleness banner on the overview is a
 * follow-up.
 */
import type { ReactElement } from "react";
import { itemKind } from "@/components/WebsiteBuilder/settings/program-manager/programShared";
import { compareProgramItems } from "@/lib/utils/programOrder";
import { resolveText } from "@/lib/utils/resolve-text";
import type { LangText } from "@/lib/utils/resolve-text";
import type { Event } from "@/types/Event";
import type { EventProgramItem } from "@/types/EventProgramItem";
import type { EventSpeaker } from "@/types/EventSpeaker";
import type { EventSponsor } from "@/types/EventSponsor";
import type { EventSponsorTier } from "@/types/EventSponsorTier";
import type { Field } from "@/types/Field";

export type EventBindingKey =
  | "event.start"
  | "event.end"
  | "event.name"
  | "event.address"
  | "event.locationName"
  | "event.description"
  | "event.onlineUrl"
  | "event.dateLine";

/**
 * The facts a TEXT child (`eventBindings.text`) may bind to — Cvent-style
 * hero widgets: title = event name, eyebrow = date line, venue name. Only
 * facts with a clean single source on the event record qualify.
 */
export const TEXT_BINDING_FACTS = [
  "event.name",
  "event.dateLine",
  "event.locationName",
] as const satisfies readonly EventBindingKey[];

export type TextBindingFact = (typeof TEXT_BINDING_FACTS)[number];

export const isTextBindingFact = (v: unknown): v is TextBindingFact =>
  (TEXT_BINDING_FACTS as readonly unknown[]).includes(v);

/** Narrow projection for getEvent/useEvent — everything bindings can read. */
export const EVENT_BINDING_PROJECTION =
  "name,start,end,timeZone,address,address2,zipCode,city,country,locationName,description,onlineUrl";

export type EventBindings = Partial<Record<string, EventBindingKey>>;

/** Composed postal address ("Location Name, Street 1, 12345 City, Country"). */
export function composeEventAddress(event: Event): string | undefined {
  const streetPart = [event.address, event.address2]
    .filter((v): v is string => Boolean(v && v.trim()))
    .join(" ");
  const cityPart = [event.zipCode, event.city]
    .filter((v): v is string => Boolean(v && v.trim()))
    .join(" ");
  const parts = [event.locationName, streetPart, cityPart, event.country].filter(
    (v): v is string => Boolean(v && v.trim()),
  );
  return parts.length > 0 ? parts.join(", ") : undefined;
}

/**
 * Google-Maps search URL for the event's real venue — replaces the template
 * footers' hardcoded German-city links at generation time (audit G7/W4).
 * `undefined` when the event has no location facts at all (caller drops the
 * link — an honest absence beats a wrong city).
 */
export function composeGoogleMapsUrl(event: Event): string | undefined {
  const query =
    composeEventAddress(event) ??
    event.locationName?.trim() ??
    event.city?.trim();
  if (!query) return undefined;
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(query)}`;
}

/** UTC instant → Google-Calendar `dates=` token (YYYYMMDDTHHMMSSZ). */
function toCalendarStamp(date: Date): string {
  return date.toISOString().replace(/[-:]/g, "").replace(/\.\d{3}Z$/, "Z");
}

/**
 * Google-Calendar template URL with the event's real name + start/end —
 * replaces the template footers' hardcoded fantasy titles (audit G7/W4).
 * End falls back to start + 1h when missing/invalid. `undefined` without a
 * name AND a valid start (caller drops the link).
 */
export function composeGoogleCalendarUrl(event: Event): string | undefined {
  const name = event.name?.trim();
  if (!name || !event.start) return undefined;
  const start = new Date(event.start);
  if (Number.isNaN(start.getTime())) return undefined;
  let end = event.end ? new Date(event.end) : undefined;
  if (!end || Number.isNaN(end.getTime()) || end.getTime() <= start.getTime()) {
    end = new Date(start.getTime() + 60 * 60 * 1000);
  }
  const params = new URLSearchParams({
    action: "TEMPLATE",
    text: name,
    dates: `${toCalendarStamp(start)}/${toCalendarStamp(end)}`,
  });
  const location = composeEventAddress(event);
  if (location) params.set("location", location);
  return `https://calendar.google.com/calendar/render?${params.toString()}`;
}

/**
 * "12. Juni 2026 · Berlin"-style line: locale-formatted start(–end) date plus
 * the venue (locationName, else city). ONE composition shared by the
 * generation-time fact bake (WebsiteConfiguratorPage → withEventFacts) and
 * the `event.dateLine` binding refresh, so a seeded hero eyebrow and its
 * later re-bakes are byte-identical. `locale` defaults to the runtime locale.
 */
export function composeEventDateLine(
  event: Event,
  locale?: string,
): string | undefined {
  if (!event.start) return undefined;
  const startDate = new Date(event.start);
  if (Number.isNaN(startDate.getTime())) return undefined;
  const fmt = new Intl.DateTimeFormat(locale, {
    day: "numeric",
    month: "long",
    year: "numeric",
    ...(event.timeZone ? { timeZone: event.timeZone } : {}),
  });
  let dateText: string;
  try {
    dateText = fmt.format(startDate);
  } catch {
    return undefined;
  }
  if (event.end) {
    const endDate = new Date(event.end);
    if (!Number.isNaN(endDate.getTime()) && fmt.format(endDate) !== dateText) {
      dateText = `${dateText} – ${fmt.format(endDate)}`;
    }
  }
  const venue = event.locationName?.trim() || event.city?.trim() || "";
  return venue ? `${dateText} · ${venue}` : dateText;
}

/** Resolves a binding key to its concrete string value, or undefined when the
 *  event lacks the data (callers keep the manual value + show a hint).
 *  `locale` only affects locale-formatted keys (event.dateLine). */
export function resolveEventBinding(
  event: Event,
  key: EventBindingKey,
  locale?: string,
): string | undefined {
  switch (key) {
    case "event.start":
      return event.start ? new Date(event.start).toISOString() : undefined;
    case "event.end":
      return event.end ? new Date(event.end).toISOString() : undefined;
    case "event.name":
      return event.name?.trim() || undefined;
    case "event.address":
      return composeEventAddress(event);
    case "event.locationName":
      return event.locationName?.trim() || undefined;
    case "event.description":
      return event.description?.trim() || undefined;
    case "event.onlineUrl":
      return event.onlineUrl?.trim() || undefined;
    case "event.dateLine":
      return composeEventDateLine(event, locale);
  }
}

/**
 * Binding value AS WRITTEN into a node prop. TextChild's `text` prop renders
 * as HTML (dangerouslySetInnerHTML in editor + runtime), so plain-text facts
 * bound onto `text` are HTML-escaped here; every other prop (location,
 * targetDate, …) passes through verbatim. The bake writes a BARE string —
 * language-neutral by the LangText contract (shown for every site language),
 * which is exactly what an event fact is. A pre-existing per-language map is
 * therefore replaced, same as the map/location binding's plain-prop write.
 */
export function resolveBoundPropValue(
  event: Event,
  propName: string,
  key: EventBindingKey,
  locale?: string,
): string | undefined {
  const raw = resolveEventBinding(event, key, locale);
  if (raw === undefined) return undefined;
  return propName === "text" ? escapeEntityHtml(raw) : raw;
}

type NodeLike = {
  data: {
    props: Record<string, unknown> & { eventBindings?: EventBindings };
    nodes?: string[];
    linkedNodes?: Record<string, string>;
    /** Craft displayName — lets a pass skip children it does not own (a
     *  hand-dropped Text node inside an Agenda section, say). */
    displayName?: string;
  };
};

type QueryLike = {
  node: (id: string) => { get: () => NodeLike | undefined };
};

/**
 * Re-bakes every bound prop across the tree from the current event data.
 * Returns the number of props that actually changed. Callers pass
 * `actions.history.ignore()` so bakes stay out of undo history; savability
 * of a bake-only session is tracked by the bakeDirty store.
 */
export function refreshBoundNodes(
  nodeIds: readonly string[],
  query: QueryLike,
  actions: {
    setProp: (nodeId: string, cb: (props: Record<string, unknown>) => void) => void;
  },
  event: Event,
  locale?: string,
): number {
  let changed = 0;
  for (const nodeId of nodeIds) {
    let node: NodeLike | undefined;
    try {
      node = query.node(nodeId).get();
    } catch {
      continue;
    }
    const bindings = node?.data.props.eventBindings;
    if (!node || !bindings) continue;
    for (const [propName, key] of Object.entries(bindings)) {
      if (!key) continue;
      const value = resolveBoundPropValue(event, propName, key, locale);
      if (value === undefined) continue;
      if (node.data.props[propName] === value) continue;
      actions.setProp(nodeId, (props) => {
        props[propName] = value;
      });
      changed += 1;
    }
  }
  return changed;
}

/* ------------------------------------------------------------------------- *
 * Program / speaker bindings (B-4)
 *
 * Unlike the prop-value bindings above, these bindings generate CHILD NODES:
 * an Agenda section carrying `programBinding: "event.program"` mirrors the
 * event's program items as AgendaChild children; a Speaker section carrying
 * `speakersBinding: "event.speakers"` mirrors the event's speakers as
 * SpeakerChild children. Generated children are tagged
 * `generatedFrom: <entityId>` so manual (untagged) children survive every
 * refresh. Values are still baked at save — the visitor runtime renders the
 * serialized children with zero binding knowledge.
 * ------------------------------------------------------------------------- */

export const PROGRAM_BINDING_KEY = "event.program" as const;
export const SPEAKERS_BINDING_KEY = "event.speakers" as const;
export const SPONSORS_BINDING_KEY = "event.sponsors" as const;

export type ProgramBindingKey = typeof PROGRAM_BINDING_KEY;
export type SpeakersBindingKey = typeof SPEAKERS_BINDING_KEY;
export type SponsorsBindingKey = typeof SPONSORS_BINDING_KEY;

/** Entity text is plain text; agenda/speaker text props render as HTML. */
export function escapeEntityHtml(value: string): string {
  return value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

/**
 * "<b>09:00 - 10:30</b>" in the event's timeZone — the shape existing agenda
 * entries use for their time text. Falls back to the runtime's local zone
 * when the event has no timeZone / the zone is invalid.
 */
export function formatProgramTimeRange(
  startTime: string,
  endTime: string | undefined,
  timeZone: string | undefined,
  /** Site's primary content language. Was hardcoded `de-DE` on BOTH paths, so
   *  an English page showed German 24-hour times — its neighbour
   *  `formatProgramDayHeading` has honoured the locale all along. */
  locale: string | undefined,
): string {
  const fmt = (iso: string): string | undefined => {
    const date = new Date(iso);
    if (Number.isNaN(date.getTime())) return undefined;
    const lang = locale || "de-DE";
    try {
      return new Intl.DateTimeFormat(lang, {
        hour: "2-digit",
        minute: "2-digit",
        timeZone,
      }).format(date);
    } catch {
      // Reached for an unusable time zone — and, in principle, an unusable
      // locale, so the fallback names a locale that cannot throw rather than
      // reusing `lang`. Deterministic on purpose: passing `undefined` here
      // would bake the ADMIN's browser locale into the published page.
      return new Intl.DateTimeFormat("de-DE", {
        hour: "2-digit",
        minute: "2-digit",
      }).format(date);
    }
  };
  const start = fmt(startTime);
  if (!start) return "";
  const end = endTime ? fmt(endTime) : undefined;
  return `<b>${end ? `${start} - ${end}` : start}</b>`;
}

/** Calendar-day key (yyyy-mm-dd) of an ISO instant in the event's zone. */
export function programDayKey(
  iso: string,
  timeZone: string | undefined,
): string | undefined {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return undefined;
  try {
    return new Intl.DateTimeFormat("en-CA", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      timeZone,
    }).format(date);
  } catch {
    return new Intl.DateTimeFormat("en-CA", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    }).format(date);
  }
}

/**
 * Day-key ordering shared by the BAKE and the admin ProgramTab, so the two
 * cannot disagree about what order the days go in.
 *
 * The bake sorts `[...byDay.keys()]` — `programDayKey` emits `yyyy-mm-dd`,
 * so lexicographic IS chronological — and flushes its undated bucket last,
 * without a heading. ProgramTab buckets the same items but used to keep
 * INSERTION order, so dragging an item across days made the admin list
 * contradict the published page. `"no-date"` is the sentinel key for items
 * with no `startTime`.
 */
export const NO_DATE_DAY_KEY = "no-date";

export function compareProgramDayKeys(a: string, b: string): number {
  if (a === b) return 0;
  if (a === NO_DATE_DAY_KEY) return 1;
  if (b === NO_DATE_DAY_KEY) return -1;
  return a < b ? -1 : 1;
}

/**
 * Localized day heading for the group-by-day bake ("Montag, 15. September" /
 * "Monday, 15 September"). Locale = the event's primary language (the baked
 * page's canonical copy language); falls back to en-GB.
 */
export function formatProgramDayHeading(
  iso: string,
  timeZone: string | undefined,
  locale: string | undefined,
): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "";
  const opts: Intl.DateTimeFormatOptions = {
    weekday: "long",
    day: "numeric",
    month: "long",
    timeZone,
  };
  const zoneless: Intl.DateTimeFormatOptions = {
    weekday: "long",
    day: "numeric",
    month: "long",
  };
  try {
    return new Intl.DateTimeFormat(locale || "en-GB", opts).format(date);
  } catch {
    // Sibling helpers drop ONLY the invalid zone and keep their locale
    // (audit fix W2A-HEADING-LOCALE-FALLBACK); en-GB is last resort for a
    // genuinely invalid locale.
    try {
      return new Intl.DateTimeFormat(locale || "en-GB", zoneless).format(date);
    } catch {
      return new Intl.DateTimeFormat("en-GB", zoneless).format(date);
    }
  }
}

/**
 * The exact props a generated AgendaChild is created/refreshed with. Every
 * key must exist on children/Agenda/Agenda.tsx AgendaProps (snapshot-tested)
 * — plus the `generatedFrom` tag.
 */
export type GeneratedAgendaChildProps = {
  generatedFrom: string;
  isVisible: boolean;
  textText1: string;
  textText2: string;
  textText3: string;
  isVisibleText3: boolean;
  textCount: "2" | "3";
  textText: string;
  isVisibleText: boolean;
  isExpandable: boolean;
  alwaysOpen: boolean;
  /** PUBLIC attachments (Wave A) — showOnWebsite-filtered at bake; old
   *  runtime snapshots ignore the prop. */
  attachments: BakedAttachment[];
};

/**
 * Kind-aware bake (W4/F6, audit decision): ALL kinds bake as agenda rows —
 * a published agenda missing the breaks/offering windows would lie about
 * the day. Per kind:
 * - SESSION: full row (time, title, speakers, expandable location/description
 *   detail + attachments) — unchanged pre-cut behavior.
 * - BREAK: title + time ONLY — no speakers, no expandable detail, no
 *   attachments. A break is structure, not content.
 * - OFFERING: title + time, speakers allowed if linked; NO "bookable" label
 *   (the bake is locale-free — a baked English label would leak into every
 *   language; slot booking lives in the runtime session widgets).
 * Bake-staleness: these props are baked at builder open+save — published
 * sites keep their OLD agenda rows (incl. pre-kind shapes) until an admin
 * re-opens the builder and saves.
 */
export function mapProgramItemToAgendaChildProps(
  item: EventProgramItem,
  speakersById: ReadonlyMap<string, EventSpeaker>,
  timeZone: string | undefined,
  /** Primary content language for the baked time range. REQUIRED so a new
   *  call site cannot quietly fall back to German again. */
  locale: string | undefined,
): GeneratedAgendaChildProps {
  const isBreak = itemKind(item) === "BREAK";
  const speakerNames = isBreak
    ? ""
    : (item.speakerIds ?? [])
        .map((id) => speakersById.get(id)?.name?.trim())
        .filter((name): name is string => Boolean(name))
        .join(", ");

  // Expanded detail = location + description, each on its own line.
  const detailParts = isBreak
    ? []
    : [item.location, item.description]
        .map((v) => v?.trim())
        .filter((v): v is string => Boolean(v))
        .map(escapeEntityHtml);
  const detail = detailParts.join("<br/>");
  const attachments = isBreak
    ? []
    : projectPublicAttachments(item.attachments);

  return {
    generatedFrom: item.programItemId ?? "",
    isVisible: true,
    textText1: formatProgramTimeRange(
      item.startTime,
      item.endTime,
      timeZone,
      locale,
    ),
    textText2: escapeEntityHtml(item.title.trim()),
    textText3: escapeEntityHtml(speakerNames),
    isVisibleText3: speakerNames.length > 0,
    textCount: speakerNames.length > 0 ? "3" : "2",
    textText: detail,
    isVisibleText: detail.length > 0,
    // Attachments live in the expanded body — expandable when EITHER exists.
    isExpandable: detail.length > 0 || attachments.length > 0,
    alwaysOpen: false,
    attachments,
  };
}

/**
 * Desired generated AgendaChildren for a bound Agenda section — optionally
 * with DAY-HEADER rows (sections-expansion W2a): when `groupByDay`, a header
 * child (tag `day:<yyyy-mm-dd>`) precedes each calendar day's items (event
 * timezone). Headers are ordinary AgendaChildren (bold date as the title
 * line, nothing else) — the visitor runtime renders them with ZERO knowledge
 * of grouping, and the tag reconciliation deletes them when the toggle goes
 * off. Existing per-item time strings are untouched (backward compat).
 *
 * Kind-aware (W4/F6): ALL kinds (SESSION/OFFERING/BREAK) produce a row —
 * the per-kind row shape is decided in mapProgramItemToAgendaChildProps.
 *
 * Track exclusion (per-section `excludedProgrammeIds` prop): items whose
 * `programmeId` is listed are dropped BEFORE mapping — ALL kinds alike
 * (SESSION/OFFERING/BREAK). Unassigned items (no programmeId — the default
 * "main track") ALWAYS pass, and so do PLENARY items (spansAllTracks=true —
 * they belong to every track, so no exclusion list can drop them; the
 * defensive programmeId guard covers corrupt rows carrying both fields).
 * Empty/absent = every track visible, so old sections without the prop bake
 * unchanged (additive). Filtering happens before day-bucketing, so a day
 * whose items are all excluded emits no header. Bake-staleness as usual:
 * published sites keep the old rows until an admin re-opens the builder and
 * saves.
 */
/** Tolerant read of a string-array selection prop. `null` = unrestricted. */
const readIdList = (raw: unknown): string[] | null =>
  Array.isArray(raw)
    ? (raw as unknown[]).filter((v): v is string => typeof v === "string")
    : null;

/**
 * Which program items a section shows, composing the two legs with AND:
 *
 *  - `excludedProgrammeIds` — a track POLICY. It keeps applying to sessions
 *    created tomorrow, and PLENARY items (`spansAllTracks`) bypass it by
 *    design: they belong to every track, so no track exclusion can drop them.
 *  - `includedProgramItemIds` — an explicit id SNAPSHOT. A stronger statement
 *    than a policy, so a plenary item IS droppable this way. Absent = no
 *    restriction; explicit `[]` = nothing.
 *
 * Kept as two props rather than materialising the exclusion into an id list:
 * that would dirty every document already carrying the prop AND silently
 * start publishing future sessions of an excluded track.
 */
export function selectProgramItemsForSection(
  sortedProgram: readonly EventProgramItem[],
  props: {
    excludedProgrammeIds?: readonly string[] | unknown;
    includedProgramItemIds?: readonly string[] | unknown;
  },
): EventProgramItem[] {
  const excludedTracks = new Set(readIdList(props.excludedProgrammeIds) ?? []);
  const included = readIdList(props.includedProgramItemIds);
  const includedSet = included ? new Set(included) : null;
  return sortedProgram.filter((item) => {
    if (!item.programItemId) return false;
    if (includedSet && !includedSet.has(item.programItemId)) return false;
    if (item.spansAllTracks === true) return true;
    return !(item.programmeId && excludedTracks.has(item.programmeId));
  });
}

/**
 * Which speakers a section shows. `includedSpeakerIds` absent = all, explicit
 * `[]` = none — the same convention as `includedSponsorIds`.
 */
export function selectSpeakersForSection(
  sortedSpeakers: readonly EventSpeaker[],
  props: { includedSpeakerIds?: readonly string[] | unknown },
): EventSpeaker[] {
  const included = readIdList(props.includedSpeakerIds);
  const includedSet = included ? new Set(included) : null;
  return sortedSpeakers.filter(
    (s) =>
      Boolean(s.speakerId) &&
      (!includedSet || includedSet.has(s.speakerId as string)),
  );
}

export function buildDesiredAgendaChildren(
  sortedProgram: readonly EventProgramItem[],
  speakersById: ReadonlyMap<string, EventSpeaker>,
  timeZone: string | undefined,
  groupByDay: boolean,
  locale: string | undefined,
  excludedProgrammeIds?: readonly string[],
  /** Explicit id pick (`includedProgramItemIds`). See the doc block above. */
  includedProgramItemIds?: readonly string[],
): DesiredChild[] {
  // Sort HERE rather than trusting the caller's precondition — that turns the
  // published order into a property of this function instead of a promise made
  // somewhere else. `compareProgramItems` is chronological with `sortIndex`
  // demoted to a tiebreak, and it puts undated items last in BOTH modes.
  const items = selectProgramItemsForSection(sortedProgram, {
    excludedProgrammeIds,
    includedProgramItemIds,
  }).sort(compareProgramItems);
  if (!groupByDay) {
    return items.map((item) => ({
      entityId: item.programItemId as string,
      props: mapProgramItemToAgendaChildProps(
        item,
        speakersById,
        timeZone,
        locale,
      ),
    }));
  }
  // REAL day-bucketing (audit fix W2A-DUP-DAY-HEADER): emitting a header on
  // every day CHANGE would duplicate a day's entityId as soon as one day's
  // items are not contiguous in the input — the tag reconciler keeps one node
  // per tag and the ordering pass could then never converge (non-idempotent
  // refresh). A chronological order cannot interleave days, so this is now
  // belt-and-braces rather than the load-bearing fix it was under the old
  // drag order; it stays because the invariant must not depend on the sort.
  // Bucket: one header per day, that day's items contiguous and in clock
  // order, days chronological, date-less items trailing without a header.
  const byDay = new Map<string, EventProgramItem[]>();
  const undated: EventProgramItem[] = [];
  for (const item of items) {
    const day = programDayKey(item.startTime, timeZone);
    if (!day) {
      undated.push(item);
      continue;
    }
    const bucket = byDay.get(day);
    if (bucket) bucket.push(item);
    else byDay.set(day, [item]);
  }
  const desired: DesiredChild[] = [];
  const pushItem = (item: EventProgramItem) =>
    desired.push({
      entityId: item.programItemId as string,
      props: mapProgramItemToAgendaChildProps(
        item,
        speakersById,
        timeZone,
        locale,
      ),
    });
  for (const day of [...byDay.keys()].sort(compareProgramDayKeys)) {
    const dayItems = byDay.get(day) as EventProgramItem[];
    const heading = formatProgramDayHeading(
      dayItems[0].startTime,
      timeZone,
      locale,
    );
    if (heading) {
      desired.push({
        entityId: `day:${day}`,
        props: {
          generatedFrom: `day:${day}`,
          isVisible: true,
          textText1: "",
          textText2: `<b>${escapeEntityHtml(heading)}</b>`,
          textText3: "",
          isVisibleText3: false,
          textCount: "2",
          textText: "",
          isVisibleText: false,
          attachments: [],
          isExpandable: false,
          alwaysOpen: false,
        },
      });
    }
    for (const item of dayItems) pushItem(item);
  }
  for (const item of undated) pushItem(item);
  return desired;
}

/**
 * Icon names per social type — restricted to the icon set SpeakerChild
 * already ships as defaults (guaranteed present in the visitor runtime's
 * icon resolver). Unknown types fall back to the globe.
 */
export const SPEAKER_SOCIAL_ICON_BY_TYPE: Record<string, string> = {
  website: "IconGlobe",
  email: "IconMail",
  phone: "IconPhone",
  linkedin: "IconBrandLinkedin",
  instagram: "IconBrandInstagram",
  twitter: "IconBrandTwitter",
};

export function speakerSocialIconName(type: string | undefined): string {
  return SPEAKER_SOCIAL_ICON_BY_TYPE[(type ?? "").toLowerCase()] ?? "IconGlobe";
}

/** A SpeakerChild social icon: iconName + href for the linked icon nodes. */
export type GeneratedSpeakerSocial = { iconName: string; href: string };

/** Max social icon slots a SpeakerChild renders (icon-0 … icon-5). */
export const SPEAKER_SOCIAL_SLOTS = 6;

/**
 * The exact props a generated SpeakerChild is created/refreshed with. Every
 * key except `generatedFrom` must exist on children/Speaker/Speaker.tsx
 * SpeakerProps (snapshot-tested). `imageUrl` stays undefined when the
 * speaker has no image so the child's placeholder default applies.
 */
export type GeneratedSpeakerChildProps = {
  generatedFrom: string;
  isVisible: boolean;
  imageUrl: string | undefined;
  textName: string;
  isVisibleName: boolean;
  textOccupation: string;
  isVisibleOccupation: boolean;
  iconCount: number;
  socialLinks: GeneratedSpeakerSocial[];
  /** PUBLIC attachments (Wave A) — showOnWebsite-filtered at bake. */
  attachments: BakedAttachment[];
  /** PUBLISHED custom field values, label + value, in definition order.
   *  Empty unless a definition is `publicOnWebsite` (the backend strips the
   *  rest before the value ever reaches the bake) and the individual speaker
   *  has not hidden it. Old per-event runtime snapshots ignore the prop. */
  customFields: BakedCustomField[];
};

/** One baked custom field: already-escaped label and value, ready to render as
 *  a text line. Empty values are dropped at bake, so a present entry always
 *  has something to show. */
export type BakedCustomField = { label: string; value: string };

/**
 * Custom values → baked label/value lines.
 *
 * Everything reaching this function is already publishable: the backend
 * projection drops values whose definition is not `publicOnWebsite`. What is
 * left to do here is the per-ENTITY strip (the popup's visibility toggle,
 * stored as `additionalFields.<name>` in `hiddenFields`), ordering, formatting
 * and escaping.
 */
export function projectPublicCustomFields(
  values: Record<string, unknown> | undefined,
  hiddenFields: unknown,
  defs: readonly Field[] | undefined,
): BakedCustomField[] {
  if (!values) return [];
  // Definition order when we have definitions — otherwise whatever order the
  // value map arrived in, which at least stays stable per read.
  //
  // hasOwnProperty, NOT `in`: field names are only constrained to
  // [a-zA-Z0-9_-] and "constructor" / "toString" / "valueOf" are all legal
  // and unreserved. `"constructor" in values` is true for every object, so a
  // field with that name would resolve to Object.prototype's constructor and
  // bake the stringified native function onto the public page.
  const has = (n: string) => Object.prototype.hasOwnProperty.call(values, n);
  const names = defs?.length
    ? defs.map((d) => d.name).filter(has)
    : Object.keys(values);
  const labelOf = (name: string) => {
    const def = defs?.find((d) => d.name === name);
    return def?.translation || name;
  };
  const out: BakedCustomField[] = [];
  for (const name of names) {
    if (hasHiddenField(hiddenFields, `additionalFields.${name}`)) continue;
    const text = formatCustomValue(values[name]);
    if (!text) continue;
    out.push({
      label: escapeEntityHtml(labelOf(name)),
      value: escapeEntityHtml(text),
    });
  }
  return out;
}

/** Custom value → display string. Arrays (MULTI_SELECT) join; booleans and
 *  numbers stringify; null/undefined/blank drop the line entirely rather than
 *  baking an orphan label. Dates arrive as ISO strings and are baked as-is —
 *  the runtime has no locale to reformat them with. */
function formatCustomValue(value: unknown): string {
  if (value === null || value === undefined) return "";
  if (Array.isArray(value)) {
    return value
      .map((v) => formatCustomValue(v))
      .filter(Boolean)
      .join(", ");
  }
  if (typeof value === "boolean") return value ? "✓" : "";
  // Anything non-scalar is dropped rather than stringified. `typeof null` is
  // "object" but null already returned above; functions are caught here too,
  // so a prototype-named key can never reach String() and leak source text.
  if (typeof value === "object" || typeof value === "function") return "";
  return String(value).trim();
}

/** Wave D display toggles — COSMETIC bake strip only (admin surfaces always
 *  show everything; the stored value is untouched). */
const hasHiddenField = (hiddenFields: unknown, key: string): boolean =>
  Array.isArray(hiddenFields) && hiddenFields.includes(key);

export function mapSpeakerToSpeakerChildProps(
  speaker: EventSpeaker,
  /** SPEAKER definitions, for LABELS and ordering only. Optional so every
   *  existing caller keeps working; without it the raw field name is the
   *  label. */
  customDefs?: readonly Field[],
): GeneratedSpeakerChildProps {
  const socials = hasHiddenField(speaker.hiddenFields, "socials")
    ? []
    : (speaker.socials ?? [])
        .filter((s) => Boolean(s?.url?.trim()))
        .slice(0, SPEAKER_SOCIAL_SLOTS)
        .map((s) => ({
          iconName: speakerSocialIconName(s.type),
          href: s.url.trim(),
        }));
  // Hidden occupation bakes empty → isVisibleOccupation false downstream.
  const occupation = hasHiddenField(speaker.hiddenFields, "occupation")
    ? ""
    : (speaker.occupation?.trim() ?? "");
  return {
    generatedFrom: speaker.speakerId ?? "",
    isVisible: true,
    imageUrl: speaker.imageUrl?.trim() || undefined,
    textName: escapeEntityHtml(speaker.name.trim()),
    isVisibleName: true,
    textOccupation: escapeEntityHtml(occupation),
    isVisibleOccupation: occupation.length > 0,
    iconCount: socials.length,
    socialLinks: socials,
    attachments: projectPublicAttachments(speaker.attachments),
    customFields: projectPublicCustomFields(
      speaker.additionalFields,
      speaker.hiddenFields,
      customDefs,
    ),
  };
}

/* --------------------------- sponsors binding ---------------------------- */

/**
 * SponsorsChild is a FLAT-prop component (no linked Text/Image leaf nodes),
 * so its generated props are the whole story — no linked-node sync twin.
 * Field bridge: entity `logoUrl`→child `src`, `linkUrl`→`href`; the entity's
 * FREE-STRING tier normalizes onto the child's 4-value band vocabulary.
 */
export type GeneratedSponsorChildProps = {
  generatedFrom: string;
  isVisible: boolean;
  src: string | undefined;
  alt: string;
  href: string;
  /** LEGACY band value — DUAL-WRITTEN next to `tierId` so per-event runtime
   *  SNAPSHOTS baked before configurable tiers keep rendering an
   *  approximation (old bundles know only these 4 bands). */
  tier: "none" | "gold" | "silver" | "bronze";
  /** Configured-tier reference (hex string, "" = no tier). Only meaningful
   *  when the section carries a `tierBands` prop (new runtime bundles). */
  tierId: string;
  /** PUBLIC attachments (Wave A) — showOnWebsite-filtered at bake. */
  attachments: BakedAttachment[];
  /** PUBLISHED custom field values — speaker twin, same rules. */
  customFields: BakedCustomField[];
};

/** Free-string entity tier → child band. PLATINUM joins gold (the top band —
 *  the child vocabulary has no platinum); unknown/empty → none. */
export function normalizeSponsorTier(
  tier: string | undefined,
): "none" | "gold" | "silver" | "bronze" {
  const t = (tier ?? "").trim().toLowerCase();
  if (t === "gold" || t === "platinum") return "gold";
  if (t === "silver") return "silver";
  if (t === "bronze") return "bronze";
  return "none";
}

/** PUBLIC attachment entries → baked child prop (Wave A): showOnWebsite-
 *  filtered, sorted, public-safe fields only. Old per-event runtime
 *  snapshots ignore the prop (graceful). Stale-until-resave like every
 *  baked value. */
export type BakedAttachment = {
  name: string;
  url: string;
  contentType: string;
  fileSize: number;
};

export function projectPublicAttachments(
  raw: unknown,
): BakedAttachment[] {
  if (!Array.isArray(raw)) return [];
  return (raw as Record<string, unknown>[])
    .filter(
      (a) =>
        !!a &&
        typeof a === "object" &&
        a.showOnWebsite !== false &&
        typeof a.url === "string" &&
        (a.url as string).length > 0,
    )
    .sort(
      (a, b) => ((a.sortIndex as number) ?? 0) - ((b.sortIndex as number) ?? 0),
    )
    .map((a) => ({
      name: (a.name as string) ?? "",
      url: a.url as string,
      contentType: (a.contentType as string) ?? "",
      fileSize: (a.fileSize as number) ?? 0,
    }));
}

/* ------------------------ configurable tiers (T-wave) --------------------- */

/** The baked `tierBands` section prop — MIRRORS the runtime's
 *  SponsorTierBand (website-builder-frontend sponsors.tsx) — keep in sync. */
export type SponsorTierBandProp = {
  id: string;
  label?: LangTextValue;
  enabled?: boolean;
  style?: string;
  /** Preset provenance (gold|silver|bronze|platinum|null) — carried on the
   *  band so the SETTINGS DRAWER can re-derive the legacy heading pairs
   *  when the user edits a band heading (the runtime ignores it). */
  presetKey?: string | null;
  /** True once the user has typed their own heading for this band.
   *
   *  Without it a seeded label is indistinguishable from an edited one, so
   *  renaming the tier could never reach the published heading: the rebuild
   *  preserved the seed forever and the section never even registered as
   *  changed. Editor-only bookkeeping — the runtime ignores it. */
  labelOverridden?: boolean | Record<string, boolean>;
};

// LangText lives in lib/utils/resolve-text — string | Record<locale,string>.
type LangTextValue = string | Record<string, string> | undefined | null;

/**
 * Legacy dual-write mapping: configured tier → 4-value band for OLD runtime
 * snapshots. Presets map 1:1 (platinum joins gold — the legacy vocabulary
 * has no platinum band); a CUSTOM tier takes the band of the nearest preset
 * tier at-or-before it in configured order, `none` when no preset precedes.
 * Rationale: old snapshots can only express order via the 4 legacy bands —
 * "custom → none" would dump every custom tier into the LAST band and
 * destroy the configured order; nearest-preceding-preset preserves a
 * monotone approximation (a custom "Partners" tier placed after Silver
 * renders in the silver band: after gold, before bronze).
 */
export function computeLegacyBandForTiers(
  tiers: readonly EventSponsorTier[],
): Map<string, "none" | "gold" | "silver" | "bronze"> {
  const presetBand = (
    key: string | null | undefined,
  ): "gold" | "silver" | "bronze" | null =>
    key === "gold" || key === "platinum"
      ? "gold"
      : key === "silver"
        ? "silver"
        : key === "bronze"
          ? "bronze"
          : null;

  const byId = new Map<string, "none" | "gold" | "silver" | "bronze">();
  let carried: "none" | "gold" | "silver" | "bronze" = "none";
  for (const tier of tiers) {
    const own = presetBand(tier.presetKey);
    if (own) carried = own;
    byId.set(tier.tierId ?? "", own ?? carried);
  }
  return byId;
}

/**
 * Which heading a tier band should carry after a refresh.
 *
 * `labelOverridden` is three-valued (see the call site) and may ALSO be a
 * per-locale map. That extra shape exists because the flag's granularity
 * used to mismatch the data's: headings are LangText (per locale), but the
 * flag was per band — so typing a German heading marked the whole band, and
 * a later tier rename then updated NO locale. English would sit on the old
 * tier name forever with nothing in the UI to reveal it.
 *
 * Per locale now: a marked locale keeps its text, an unmarked one follows
 * the tier name. A stored boolean still means what it always meant, so no
 * migration and no silent clobber of existing overrides.
 */
function resolveBandLabel(
  prev: SponsorTierBandProp | undefined,
  tierName: string,
): LangTextValue {
  if (prev === undefined) return tierName;
  const owned = prev.labelOverridden;
  if (owned === false) return tierName;
  if (owned === undefined) return prev.label ?? tierName;
  if (owned === true) return prev.label ?? tierName;

  // Per-locale map: rebuild the LangText, re-seeding unmarked locales.
  const label = prev.label;
  if (label === undefined || label === null || typeof label === "string") {
    // A bare string predates any per-locale edit; only an explicitly
    // marked locale could own it, and there is none here.
    return tierName;
  }
  const out: Record<string, string> = {};
  for (const [locale, text] of Object.entries(label)) {
    out[locale] = owned[locale] === true ? text : tierName;
  }
  return out;
}

/**
 * Desired `tierBands` section prop from the configured tiers, PRESERVING the
 * user's per-band heading overrides (enabled toggle + LangText label edited
 * in SponsorsSettings) from the section's current prop — the refresher must
 * never stomp them. New bands default to disabled with the tier NAME as a
 * language-neutral label seed; `style` is always config-owned.
 */
export function buildTierBandsSectionProp(
  tiers: readonly EventSponsorTier[],
  existing: unknown,
): SponsorTierBandProp[] {
  const existingById = new Map<string, SponsorTierBandProp>();
  if (Array.isArray(existing)) {
    for (const band of existing) {
      if (band && typeof band === "object" && typeof (band as SponsorTierBandProp).id === "string") {
        existingById.set((band as SponsorTierBandProp).id, band as SponsorTierBandProp);
      }
    }
  }
  return tiers
    .filter((t) => Boolean(t.tierId))
    .map((tier) => {
      const prev = existingById.get(tier.tierId as string);
      // Label ownership, three-valued ON PURPOSE:
      //   true      → the user typed this heading; a tier rename must not
      //               overwrite it.
      //   false     → seeded by us and untouched; it FOLLOWS the tier name,
      //               so renaming a tier reaches the published heading.
      //   undefined → a band written before this flag existed. We cannot
      //               tell a seed from an edit, so we preserve it. Old
      //               events keep their headings (no silent clobber) and
      //               converge to the new behaviour once the heading is
      //               next edited or the band is recreated.
      const label = resolveBandLabel(prev, tier.name);
      return {
        id: tier.tierId as string,
        label,
        enabled: prev?.enabled ?? false,
        style: tier.style ?? "standard",
        presetKey: tier.presetKey ?? null,
        // Seed the flag explicitly on fresh bands so they are renameable;
        // legacy bands keep it absent until the user edits the heading.
        ...(prev === undefined
          ? { labelOverridden: false }
          : prev.labelOverridden !== undefined
            ? { labelOverridden: prev.labelOverridden }
            : {}),
      };
    });
}

/**
 * Legacy heading props maintained next to `tierBands` for old snapshots:
 * a legacy band's heading is enabled (with the band's label) only when
 * EXACTLY ONE configured tier maps to it — two merged tiers under one
 * legacy band would render a misleading single heading, so merged bands
 * disable it (grouped logos, no heading). Computed FROM the bands array
 * (presetKey carry, same nearest-preceding-preset rule as
 * computeLegacyBandForTiers) so the SETTINGS DRAWER can re-sync the pairs
 * on a heading edit without the tier entities in scope. Only the three
 * heading pairs are returned; callers spread them into the section props.
 */
export function buildLegacyHeadingProps(
  bands: readonly SponsorTierBandProp[],
): Record<string, unknown> {
  const presetBand = (
    key: string | null | undefined,
  ): "gold" | "silver" | "bronze" | null =>
    key === "gold" || key === "platinum"
      ? "gold"
      : key === "silver"
        ? "silver"
        : key === "bronze"
          ? "bronze"
          : null;

  const byBand: Record<"gold" | "silver" | "bronze", SponsorTierBandProp[]> = {
    gold: [],
    silver: [],
    bronze: [],
  };
  let carried: "gold" | "silver" | "bronze" | null = null;
  for (const band of bands) {
    const own = presetBand(band.presetKey);
    if (own) carried = own;
    const legacy = own ?? carried;
    if (legacy) byBand[legacy].push(band);
  }
  const props: Record<string, unknown> = {};
  (["gold", "silver", "bronze"] as const).forEach((legacy) => {
    const key = legacy.charAt(0).toUpperCase() + legacy.slice(1);
    const mapped = byBand[legacy];
    if (mapped.length === 1) {
      props[`tier${key}Enabled`] = mapped[0].enabled === true;
      props[`tier${key}Label`] = mapped[0].label;
    } else {
      props[`tier${key}Enabled`] = false;
    }
  });
  return props;
}

export function mapSponsorToSponsorChildProps(
  sponsor: EventSponsor,
  /** Non-null = a tier config exists: dual-write tierId + the mapped legacy
   *  band. Null/absent = legacy-only path (no config), byte-identical to
   *  the pre-T-wave behavior. */
  legacyBandByTierId?: Map<
    string,
    "none" | "gold" | "silver" | "bronze"
  > | null,
  /** SPONSOR definitions, for LABELS and ordering only (speaker twin). */
  customDefs?: readonly Field[],
): GeneratedSponsorChildProps {
  // Wave D: hidden tier drops the sponsor to the no-tier band (it does NOT
  // disappear); hidden linkUrl bakes an inert card (href "").
  const tierHidden = hasHiddenField(sponsor.hiddenFields, "tier");
  const configuredTierId =
    !tierHidden &&
    legacyBandByTierId &&
    sponsor.tierId &&
    legacyBandByTierId.has(sponsor.tierId)
      ? sponsor.tierId
      : "";
  return {
    generatedFrom: sponsor.sponsorId ?? "",
    isVisible: true,
    src: sponsor.logoUrl?.trim() || undefined,
    alt: sponsor.name.trim(),
    href: hasHiddenField(sponsor.hiddenFields, "linkUrl")
      ? ""
      : (sponsor.linkUrl?.trim() ?? ""),
    // With a config, tierId is the truth: assigned → mapped legacy band,
    // unassigned → none (matching the new runtime's no-tier band; the old
    // free string deliberately stops mattering once a config exists).
    tier: tierHidden
      ? "none"
      : legacyBandByTierId
        ? configuredTierId
          ? (legacyBandByTierId.get(configuredTierId) ?? "none")
          : "none"
        : normalizeSponsorTier(sponsor.tier),
    tierId: configuredTierId,
    attachments: projectPublicAttachments(sponsor.attachments),
    customFields: projectPublicCustomFields(
      sponsor.additionalFields,
      sponsor.hiddenFields,
      customDefs,
    ),
  };
}

/**
 * Per-section sponsor selection (the "use a subset" filters). Every leg is
 * AND-composed:
 *   - includedSponsorIds: explicit id picks. Absent/undefined = no
 *     restriction; explicit `[]` = NONE selected (matches the attendee-app
 *     scope convention — the select-all toggle writes `[]` when turned off).
 *   - sponsorFilterTiers: normalized band values ("gold"/"silver"/"bronze"/"none");
 *     absent or empty = no restriction (the controls collapse empty → undefined)
 *   - sponsorFilterTags: entity tags, any-overlap, case-insensitive; absent or
 *     empty = no restriction
 */
export function filterSponsorsForSection(
  sponsors: readonly EventSponsor[],
  props: Record<string, unknown>,
  legacyBandByTierId?: Map<string, "none" | "gold" | "silver" | "bronze"> | null,
): EventSponsor[] {
  const ids = Array.isArray(props.includedSponsorIds)
    ? new Set((props.includedSponsorIds as unknown[]).filter((v): v is string => typeof v === "string"))
    : null;
  // Tier selection entries: 24-hex values reference CONFIGURED tiers and
  // compare against sponsor.tierId; anything else is a legacy band value
  // normalized to the 4-band vocabulary (platinum→gold), matching the
  // attendee-backend (content-repo applySponsorSelection) so both surfaces
  // agree. A sponsor matches when EITHER leg matches (mixed configs work).
  const rawTierEntries = Array.isArray(props.sponsorFilterTiers)
    ? (props.sponsorFilterTiers as unknown[]).filter(
        (v): v is string => typeof v === "string",
      )
    : null;
  const tierIdEntries = rawTierEntries
    ? new Set(rawTierEntries.filter((v) => /^[0-9a-fA-F]{24}$/.test(v)))
    : null;
  const tiers = rawTierEntries
    ? new Set(
        rawTierEntries
          .filter((v) => !/^[0-9a-fA-F]{24}$/.test(v))
          .map((v) => normalizeSponsorTier(v)),
      )
    : null;
  const tags = Array.isArray(props.sponsorFilterTags)
    ? new Set(
        (props.sponsorFilterTags as unknown[])
          .filter((v): v is string => typeof v === "string")
          .map((v) => v.trim().toLowerCase())
          .filter((v) => v.length > 0),
      )
    : null;

  return sponsors.filter((sponsor) => {
    if (ids && !ids.has(sponsor.sponsorId ?? "")) return false;
    const tierRestricted =
      (tiers && tiers.size > 0) || (tierIdEntries && tierIdEntries.size > 0);
    if (tierRestricted) {
      // The band MUST be derived exactly as `mapSponsorToSponsorChildProps`
      // derives it for the bake, or the filter and the wall disagree about
      // what a sponsor is. They used to: with a tier config active the bake
      // treats an UNASSIGNED sponsor as "none" (the legacy free string
      // stops mattering), but this filter still read that string — so a
      // sponsor carrying legacy `tier: "BRONZE"` and no `tierId` baked into
      // the untiered band while the "Untiered" checkbox refused to match
      // it. It silently vanished from the wall with no way to select it.
      const tierHidden = hasHiddenField(sponsor.hiddenFields, "tier");
      // EFFECTIVE tier id — the same value the bake calls `configuredTierId`
      // (mapSponsorToSponsorChildProps). It is "" when the tier is hidden or
      // when the sponsor points at a tier that is no longer configured, both
      // of which the bake renders in the trailing no-tier band.
      //
      // Deriving the BAND from this but then matching on the RAW `tierId`
      // was the remaining half of this bug: a hidden-tier or dangling-tier
      // sponsor rendered untiered, yet "Untiered" refused to match it (its
      // raw tierId is still set) while its old tier's checkbox still did.
      // Both legs must speak the effective value.
      const configuredTierId =
        !tierHidden &&
        legacyBandByTierId &&
        sponsor.tierId &&
        legacyBandByTierId.has(sponsor.tierId)
          ? sponsor.tierId
          : "";
      // Without a config the legacy free string is still the truth, and a
      // hex selection entry compares against the raw id.
      const effectiveTierId = legacyBandByTierId
        ? configuredTierId
        : tierHidden
          ? ""
          : (sponsor.tierId ?? "");
      const legacyBand = tierHidden
        ? "none"
        : legacyBandByTierId
          ? configuredTierId
            ? (legacyBandByTierId.get(configuredTierId) ?? "none")
            : "none"
          : normalizeSponsorTier(sponsor.tier);
      // "none" only matches sponsors WITHOUT an effective tier — an assigned
      // sponsor is never "untiered" just because its legacy string is empty
      // (mirror of the attendee-backend applySponsorSelection).
      const legacyMatch =
        !!tiers &&
        tiers.size > 0 &&
        tiers.has(legacyBand) &&
        (legacyBand !== "none" || !effectiveTierId);
      const idMatch =
        !!tierIdEntries &&
        tierIdEntries.size > 0 &&
        !!effectiveTierId &&
        tierIdEntries.has(effectiveTierId);
      if (!legacyMatch && !idMatch) return false;
    }
    if (tags && tags.size > 0) {
      const sponsorTags = (sponsor.tags ?? []).map((tag) => tag.trim().toLowerCase());
      if (!sponsorTags.some((tag) => tags.has(tag))) return false;
    }
    return true;
  });
}

/* --------------------------- refresh machinery --------------------------- */

type ProgramActionsLike = {
  setProp: (
    nodeId: string,
    cb: (props: Record<string, unknown>) => void,
  ) => void;
  delete: (nodeId: string) => void;
  move: (nodeId: string, targetParentId: string, index: number) => void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- structural
  // stand-in for the engine's NodeTree; method syntax keeps it bivariant so
  // the real EditorActions satisfies this shape.
  addNodeTree(tree: any, parentId: string): void;
};

type ProgramQueryLike = QueryLike & {
  getNodes: () => Record<string, NodeLike | undefined>;
  parseReactElement: (element: ReactElement) => { toNodeTree: () => unknown };
};

export type ProgramBindingElementFactories = {
  makeAgendaChildElement: (props: GeneratedAgendaChildProps) => ReactElement;
  makeSpeakerChildElement: (props: GeneratedSpeakerChildProps) => ReactElement;
  makeSponsorChildElement: (props: GeneratedSponsorChildProps) => ReactElement;
};

export type ProgramBindingData = {
  /** `undefined` = this entity type's data is UNAVAILABLE (query failed) —
   *  sections bound to it are left untouched. `[]` still means "zero
   *  entities" and deletes all generated children of that kind. Agenda
   *  sections need BOTH program and speakers (baked speaker names). */
  program?: readonly EventProgramItem[];
  speakers?: readonly EventSpeaker[];
  sponsors?: readonly EventSponsor[];
  /** Configured sponsor tiers. `undefined` = UNAVAILABLE (query failed) →
   *  sponsor sections keep their current tier props untouched; `[]` = no
   *  config → legacy 4-band path (pre-T-wave behavior). Non-empty = dynamic
   *  bands: bake `tierBands` on bound sections + dual-write children. */
  sponsorTiers?: readonly EventSponsorTier[];
  timeZone: string | undefined;
  /** Locale for baked day headings (event primary language; en-GB fallback). */
  locale?: string;
  /** Restores the SpeakerChild image placeholder when a speaker's photo is
   *  cleared (linked Image nodes are creation-time snapshots). */
  speakerImagePlaceholder?: string;
  /** SPEAKER / SPONSOR custom-field DEFINITIONS, used only to LABEL the baked
   *  custom values (`translation` falls back to `name`). They do not decide
   *  what gets published — the backend already strips every value whose
   *  definition is not `publicOnWebsite`, so anything reaching here is
   *  publishable. Absent = label with the raw field name. */
  speakerCustomDefs?: readonly Field[];
  sponsorCustomDefs?: readonly Field[];
  /**
   * Have the custom-field DEFINITIONS resolved? `false` parks that kind's
   * sections (baking without labels would publish raw identifiers and, since
   * the definitions are not part of the entity signature, never correct
   * itself). Absent = "not gated" — the definitions are only a bake input for
   * speaker and sponsor CARDS, never for agenda rows.
   */
  speakerCustomDefsReady?: boolean;
  sponsorCustomDefsReady?: boolean;
};

const isRecord = (v: unknown): v is Record<string, unknown> =>
  typeof v === "object" && v !== null;

/** Props the binding writes ONLY at child creation. Visibility toggles are
 *  user overrides once the child exists — the reconcile diff/write skips
 *  them so they survive re-bakes. Wave D audit D1: sponsor `tier` LEFT this
 *  set — with tier create-only, a hidden tier (hiddenFields) or an entity
 *  tier change could never move an existing child's band on the legacy
 *  path (the app stripped live while the website kept the old band).
 *  Entity tier is now the single source of truth for BOUND children on
 *  BOTH paths; manual band picks belong to unbound children (the bound
 *  child picker is hidden — SponsorsSettings). */
const CREATE_ONLY_PROPS: Record<
  "agenda" | "speaker" | "sponsor",
  ReadonlySet<string>
> = {
  agenda: new Set(["isVisible"]),
  speaker: new Set(["isVisible", "isVisibleName"]),
  sponsor: new Set(["isVisible"]),
};

/**
 * Baked props whose value is a freshly built ARRAY on every bake. Identity
 * comparison would report a diff every single time, rewriting every generated
 * child on every editor open — which marks the document bake-dirty and lights
 * up Save on a page nobody edited. `customFields` was missing here and did
 * exactly that for every speaker and sponsor child.
 */
const STRUCTURAL_PROPS: ReadonlySet<string> = new Set([
  "socialLinks",
  "attachments",
  "customFields",
]);

/**
 * A per-locale text map, as `setLangText` produces and the BACKEND's website
 * translation job writes (TemplateTranslateJobService lists `textName`,
 * `textOccupation`, `textText1…3` among its translatable keys). Detected by
 * SHAPE rather than by prop name so the linked Text nodes — whose prop is
 * plainly `text` — are covered by the same rule.
 */
const isLangMap = (v: unknown): v is Record<string, string> =>
  typeof v === "object" && v !== null && !Array.isArray(v);

/**
 * The bake speaks bare strings; a translated prop is a MAP. Comparing the two
 * with `!==` always reported a diff, so every bake replaced the map with the
 * primary-language string and every translation on a bound section was lost —
 * and the loss marked the document dirty, so the user saved it. Comparing
 * THROUGH the map fixes that: while the entity still agrees with the primary
 * language, nothing is written and the translations stand.
 *
 * The WRITE stays blunt on purpose — a bare string, the map dropped. Merging
 * the new value into the map was tried and is worse in two ways. It does not
 * converge: `resolveText` treats an empty string as missing, so writing an
 * empty primary key falls through to another locale and the next compare
 * differs again, forever — an editor reporting unsaved changes on every open
 * and a deleted value still resolving for every language. And for a derived
 * prop (`textText1` is a formatted time range) a preserved secondary locale is
 * not a stale translation but a wrong fact. Dropping the map costs the
 * translation of the one field that actually changed; the alternative costs
 * correctness in every language that did not.
 *
 * Without a primary language (no content-language context) the old identity
 * behaviour stands.
 */
const langAwareDiffers = (
  current: unknown,
  next: unknown,
  primary: string | undefined,
): boolean =>
  isLangMap(current) && typeof next === "string" && primary
    ? resolveText(current as LangText, primary, primary) !== next
    : current !== next;

/**
 * The desired keys whose value actually differs — NOT a boolean.
 *
 * `writeProps` used to take the whole desired set whenever `.some()` said any
 * key differed, which quietly made the wave's per-field rule a lie: moving a
 * session's end time rewrote its translated TITLE too, because the title rode
 * along in the same write. With a locale-aware time format that is not a rare
 * case — it is every agenda row on every non-German site, on the first bake.
 */
function changedPropKeys(
  current: Record<string, unknown>,
  desired: Record<string, unknown>,
  primary: string | undefined,
): string[] {
  return Object.entries(desired)
    .filter(([key, value]) => {
      // A key the child does not carry AT ALL is always written, even when the
      // comparison below would call it equal (an absent `attachments` vs the
      // desired `[]`). Materialising the full generated shape is pre-existing
      // behaviour; narrowing the WRITE was only ever meant to stop a changed
      // prop from dragging its siblings' translations along.
      if (!(key in current)) return true;
      if (STRUCTURAL_PROPS.has(key)) {
        // Array props — compare structurally (identity compare would rewrite
        // every generated child on every bake → bakeDirty churn).
        return JSON.stringify(current[key] ?? []) !== JSON.stringify(value ?? []);
      }
      return langAwareDiffers(current[key], value, primary);
    })
    .map(([key]) => key);
}

/** setProp the DRIFTED keys onto the child node in a single mutation. */
function writeProps(
  actions: ProgramActionsLike,
  nodeId: string,
  desired: Record<string, unknown>,
  keys: readonly string[],
): void {
  actions.setProp(nodeId, (props) => {
    for (const key of keys) {
      props[key] = desired[key];
    }
  });
}

/** Update a linked node's prop when it exists and differs. */
/**
 * Returns TRUE when it actually wrote. The caller folds that into the bake's
 * `changed` count: a run that only corrects linked nodes used to report zero,
 * so `markBakeDirty()` never fired and Save stayed grey while the editor and
 * the stored `websiteJson` had already drifted apart. The `===` short-circuit
 * below is what keeps a no-op bake at zero.
 */
function syncLinkedProp(
  query: ProgramQueryLike,
  actions: ProgramActionsLike,
  parent: NodeLike,
  linkedKey: string,
  propName: string,
  value: unknown,
  primary: string | undefined,
): boolean {
  const linkedId = parent.data.linkedNodes?.[linkedKey];
  if (!linkedId) return false;
  let linked: NodeLike | undefined;
  try {
    linked = query.node(linkedId).get();
  } catch {
    return false;
  }
  if (!linked) return false;
  const current = linked.data.props[propName];
  if (!langAwareDiffers(current, value, primary)) return false;
  actions.setProp(linkedId, (props) => {
    props[propName] = value;
  });
  return true;
}

/**
 * Element JSX props only apply at linked-node CREATION time — updating an
 * existing generated child therefore writes both the parent child props AND
 * the linked Text/Image/Icon node props directly.
 */
function syncAgendaLinkedNodes(
  query: ProgramQueryLike,
  actions: ProgramActionsLike,
  child: NodeLike,
  desired: GeneratedAgendaChildProps,
  primary: string | undefined,
): number {
  let written = 0;
  const write = (linkedKey: string, propName: string, value: unknown) => {
    if (
      syncLinkedProp(query, actions, child, linkedKey, propName, value, primary)
    ) {
      written += 1;
    }
  };
  write("text1", "text", desired.textText1);
  write("text2", "text", desired.textText2);
  write("text3", "text", desired.textText3);
  write("textExpanded", "text", desired.textText);
  return written;
}

function syncSpeakerLinkedNodes(
  query: ProgramQueryLike,
  actions: ProgramActionsLike,
  child: NodeLike,
  desired: GeneratedSpeakerChildProps,
  primary: string | undefined,
  speakerImagePlaceholder?: string,
): number {
  let written = 0;
  const write = (linkedKey: string, propName: string, value: unknown) => {
    if (
      syncLinkedProp(query, actions, child, linkedKey, propName, value, primary)
    ) {
      written += 1;
    }
  };
  // A cleared photo (imageUrl undefined) must still propagate — otherwise the
  // published site keeps the removed photo forever. Fall back to the
  // SpeakerChild placeholder; only skip when no placeholder is known.
  const imageSrc = desired.imageUrl ?? speakerImagePlaceholder;
  if (imageSrc !== undefined) {
    write("image", "src", imageSrc);
  }
  write("name", "text", desired.textName);
  write("occupation", "text", desired.textOccupation);
  for (let i = 0; i < SPEAKER_SOCIAL_SLOTS; i++) {
    const social = desired.socialLinks[i];
    if (!social) {
      // Slot beyond iconCount (fewer socials, or Wave D hidden socials):
      // the icon is display-hidden, but its linked anchor node still
      // serializes into the PUBLIC websiteJson — clear the href so old
      // URLs never ride a published bundle (audit D2). iconName stays
      // (cosmetic; node is invisible).
      write(`icon-${i}`, "href", "");
      continue;
    }
    write(`icon-${i}`, "iconName", social.iconName);
    write(`icon-${i}`, "href", social.href);
    write(`icon-${i}`, "showLinkSettings", true);
  }
  return written;
}

type DesiredChild = {
  entityId: string;
  props:
    | GeneratedAgendaChildProps
    | GeneratedSpeakerChildProps
    | GeneratedSponsorChildProps;
};

/**
 * Reconcile ONE bound section's generated children against the desired
 * entity list: delete stale tags, update drifted ones (parent props +
 * linked nodes), append missing ones, then restore entity order. Manual
 * (untagged) children are never touched; generated children live in one
 * contiguous block after them.
 */
function syncBoundSection(
  sectionId: string,
  desired: DesiredChild[],
  kind: "agenda" | "speaker" | "sponsor",
  query: ProgramQueryLike,
  actions: ProgramActionsLike,
  factories: ProgramBindingElementFactories,
  /** Primary content language — decides how a TRANSLATED (per-locale map)
   *  prop is compared and updated instead of being flattened to a string. */
  primary: string | undefined,
  speakerImagePlaceholder?: string,
  /** T-wave: with a tier CONFIG present, sponsor children's tier/tierId are
   *  reconciled (config is the truth; re-bakes must move bands on
   *  reassignment) — this overrides the kind's CREATE_ONLY_PROPS set. */
  createOnlyOverride?: ReadonlySet<string>,
): number {
  let section: NodeLike | undefined;
  try {
    section = query.node(sectionId).get();
  } catch {
    return 0;
  }
  if (!section) return 0;

  let changed = 0;
  const childIds = section.data.nodes ?? [];
  // entityId -> child node (first occurrence wins; duplicates count as stale).
  const generatedByEntity = new Map<string, { nodeId: string; node: NodeLike }>();
  const staleNodeIds: string[] = [];
  const desiredIds = new Set(desired.map((d) => d.entityId));

  for (const childId of childIds) {
    let child: NodeLike | undefined;
    try {
      child = query.node(childId).get();
    } catch {
      continue;
    }
    const tag = child?.data.props.generatedFrom;
    if (!child || typeof tag !== "string" || tag.length === 0) continue;
    if (!desiredIds.has(tag) || generatedByEntity.has(tag)) {
      staleNodeIds.push(childId);
    } else {
      generatedByEntity.set(tag, { nodeId: childId, node: child });
    }
  }

  for (const staleId of staleNodeIds) {
    actions.delete(staleId);
    changed += 1;
  }

  for (const { entityId, props } of desired) {
    const existing = generatedByEntity.get(entityId);
    if (existing) {
      const createOnly = createOnlyOverride ?? CREATE_ONLY_PROPS[kind];
      const desiredProps = Object.fromEntries(
        Object.entries(props as unknown as Record<string, unknown>).filter(
          ([key]) => !createOnly.has(key),
        ),
      );
      const drifted = changedPropKeys(
        existing.node.data.props,
        desiredProps,
        primary,
      );
      if (drifted.length > 0) {
        writeProps(actions, existing.nodeId, desiredProps, drifted);
        changed += 1;
      }
      // Linked nodes are creation-time snapshots — always re-check them.
      // SponsorsChild is flat (no linked nodes): writeProps above is enough.
      // Their writes COUNT: they are the ones that revert a hand-edit on the
      // canvas, and a revert the dirty flag never heard about left the stored
      // document and the editor disagreeing with Save greyed out.
      if (kind === "agenda") {
        changed += syncAgendaLinkedNodes(
          query,
          actions,
          existing.node,
          props as GeneratedAgendaChildProps,
          primary,
        );
      } else if (kind === "speaker") {
        changed += syncSpeakerLinkedNodes(
          query,
          actions,
          existing.node,
          props as GeneratedSpeakerChildProps,
          primary,
          speakerImagePlaceholder,
        );
      }
    } else {
      const element =
        kind === "agenda"
          ? factories.makeAgendaChildElement(props as GeneratedAgendaChildProps)
          : kind === "speaker"
            ? factories.makeSpeakerChildElement(
                props as GeneratedSpeakerChildProps,
              )
            : factories.makeSponsorChildElement(
                props as GeneratedSponsorChildProps,
              );
      actions.addNodeTree(query.parseReactElement(element).toNodeTree(), sectionId);
      changed += 1;
    }
  }

  // Order pass: if the surviving generated children don't already appear in
  // entity order, append them (in order) after all other children.
  try {
    const fresh = query.node(sectionId).get();
    const freshChildIds = fresh?.data.nodes ?? [];
    const tagByNodeId = new Map<string, string>();
    for (const childId of freshChildIds) {
      let child: NodeLike | undefined;
      try {
        child = query.node(childId).get();
      } catch {
        continue;
      }
      const tag = child?.data.props.generatedFrom;
      if (typeof tag === "string" && tag.length > 0) {
        tagByNodeId.set(childId, tag);
      }
    }
    const currentSequence = freshChildIds
      .filter((id) => tagByNodeId.has(id))
      .map((id) => tagByNodeId.get(id) as string);
    const desiredSequence = desired.map((d) => d.entityId);
    if (currentSequence.join("\u0000") !== desiredSequence.join("\u0000")) {
      const nodeIdByTag = new Map(
        [...tagByNodeId.entries()].map(([nodeId, tag]) => [tag, nodeId]),
      );
      for (const entityId of desiredSequence) {
        const nodeId = nodeIdByTag.get(entityId);
        if (!nodeId) continue;
        const liveChildren = query.node(sectionId).get()?.data.nodes ?? [];
        actions.move(nodeId, sectionId, liveChildren.length);
      }
      changed += 1;
    }
  } catch {
    // Ordering is cosmetic — never fail the refresh over it.
  }

  return changed;
}

/**
 * Child prop: the binding hid this child, and the INVERSE transition must
 * bring it back. Tri-state on purpose:
 *
 *   absent  — free; the binding may manage this child's visibility
 *   true    — the binding hid it; restore on the inverse transition
 *   false   — the user overrode that decision; never manage it again
 *
 * Without the `false` latch, a user re-showing a suppressed child would see
 * the very next bake hide it again (manual + unmarked + visible is exactly
 * the suppression case) — an unwinnable loop.
 */
export const SUPPRESSED_BY_BINDING_PROP = "suppressedByBinding";

/**
 * Section prop: the mode this section's children were last projected for.
 * ABSENT means "a document written before the mode switch existed" — the
 * unbind transition never fires for those, which is what keeps sections that
 * went through the OLD unbind path (baked children silently adopted as
 * visible manual content) exactly as they are today.
 */
export const BINDING_VISIBILITY_PROP = "bindingVisibility";

export type BindingVisibilityMode = "bound" | "unbound" | "unbound-steady";

/** Section craft displayName → the child displayName it owns. */
const SECTION_CHILD_DISPLAY_NAME: Record<string, string> = {
  Agenda: "AgendaChild",
  Speaker: "SpeakerChild",
  Sponsors: "SponsorsChild",
};

/**
 * Project one section's children onto the current binding MODE.
 *
 * Bound, the event's items are the visible set and hand-made children are
 * hidden; unbound it is the other way round. Nothing is ever deleted — the
 * generated children keep their `generatedFrom` tags while hidden, so a
 * re-bind matches them by tag and creates no duplicates.
 *
 * `isVisible` (not a section-level flag) is the lever because the visitor
 * runtime lives in another repo and renders a BAKED document: every already
 * published snapshot honours `isVisible === false` today, while a new section
 * flag would need a runtime release and still leave old snapshots stacking
 * manual + generated.
 *
 * Every rule below is a function of the child's current props alone and every
 * post-state is a fixed point, so a second run writes nothing.
 */
export function applySectionBindingVisibility(
  sectionId: string,
  mode: BindingVisibilityMode,
  opts: {
    /** Children with a DIFFERENT craft displayName are skipped: a
     *  hand-dropped Text node inside an Agenda section is nobody's item. */
    childDisplayName?: string;
  },
  query: ProgramQueryLike,
  actions: ProgramActionsLike,
): number {
  let section: NodeLike | undefined;
  try {
    section = query.node(sectionId).get();
  } catch {
    return 0;
  }
  if (!section) return 0;

  let changed = 0;
  const hide = (childId: string) => {
    actions.setProp(childId, (p) => {
      p.isVisible = false;
      p[SUPPRESSED_BY_BINDING_PROP] = true;
    });
    changed += 1;
  };
  const restore = (childId: string) => {
    actions.setProp(childId, (p) => {
      p.isVisible = true;
      delete p[SUPPRESSED_BY_BINDING_PROP];
    });
    changed += 1;
  };
  const latch = (childId: string) => {
    actions.setProp(childId, (p) => {
      p[SUPPRESSED_BY_BINDING_PROP] = false;
    });
    changed += 1;
  };

  for (const childId of section.data.nodes ?? []) {
    let child: NodeLike | undefined;
    try {
      child = query.node(childId).get();
    } catch {
      continue;
    }
    if (!child) continue;
    const childName = child.data.displayName;
    if (
      opts.childDisplayName &&
      typeof childName === "string" &&
      childName.length > 0 &&
      childName !== opts.childDisplayName
    ) {
      continue;
    }
    const props = child.data.props;
    const tag = props.generatedFrom;
    const generated = typeof tag === "string" && tag.length > 0;
    const hidden = props.isVisible === false;
    const mark = props[SUPPRESSED_BY_BINDING_PROP];

    if (mode === "bound") {
      if (generated) {
        // Coming back from an unbind. A generated child the user hid on
        // purpose carries no mark and stays hidden.
        if (mark === true) restore(childId);
        continue;
      }
      if (mark === false) continue; // user pinned it visible, permanently
      if (mark === true) {
        // BOUND means bound, whatever the event currently holds. An empty
        // event used to give the hand-made set back here, which made the
        // state PATH-DEPENDENT (binding an empty event showed the samples,
        // deleting the last item while bound hid them) and — worse — served
        // a template's invented speakers under a section that claims to come
        // from the event, all the way onto the published page.
        if (hidden) continue; // steady state
        latch(childId); // user re-showed it → the user wins from now on
        continue;
      }
      // Unmarked and already hidden = hidden BEFORE the binding was switched
      // on. Not ours; never mark it, so unbinding will not resurrect it.
      if (hidden) continue;
      hide(childId);
      continue;
    }

    if (mode === "unbound") {
      if (generated) {
        if (mark !== undefined) continue;
        if (hidden) continue; // hidden by the user while bound → leave it
        hide(childId);
        continue;
      }
      if (mark === true) restore(childId);
      continue;
    }

    // "unbound-steady": repair only — this pass NEVER hides anything, which
    // is what stops it from fighting the user between transitions.
    if (generated) {
      if (mark === true && !hidden) latch(childId);
      continue;
    }
    if (mark === true) restore(childId);
  }

  return changed;
}

/**
 * Walks all nodes, finds Agenda sections bound via `programBinding` and
 * Speaker sections bound via `speakersBinding`, and reconciles their
 * generated children with the current entities. Idempotent — writes only on
 * an actual diff. Returns the number of mutations performed.
 */
export function refreshProgramBindings(
  query: ProgramQueryLike,
  actions: ProgramActionsLike,
  data: ProgramBindingData,
  factories: ProgramBindingElementFactories,
): number {
  // Per-kind availability (undefined = query failed → leave those sections
  // untouched; defaulting to [] here WITHOUT the branch guards below would
  // mass-delete generated children of a merely-unavailable kind).
  //
  // The custom-field DEFINITIONS gate ONLY the kinds that bake custom values
  // (speaker/sponsor cards). Agenda bakes speaker NAMES only, so it must not
  // wait on them — the caller used to withhold `speakers` until the SPEAKER
  // defs query resolved, which silently blocked the agenda bake on an
  // unrelated query, and because the defs are not part of the agenda
  // signature the section then never became stale again for that session.
  const canAgenda = data.program !== undefined && data.speakers !== undefined;
  const canSpeaker =
    data.speakers !== undefined && data.speakerCustomDefsReady !== false;
  const canSponsor =
    data.sponsors !== undefined && data.sponsorCustomDefsReady !== false;

  const speakers = data.speakers ?? [];
  const speakersById = new Map(
    speakers
      .filter((s) => Boolean(s.speakerId))
      .map((s) => [s.speakerId as string, s]),
  );

  // NOT sorted here: `buildDesiredAgendaChildren` owns the programme order
  // (chronological, `sortIndex` only breaking ties) and applies it to whatever
  // it is handed. A second sort on this side would be dead code that reads as
  // the contract — revertible to sortIndex without a single test moving.
  // Speakers and sponsors DO sort here: they keep their drag order, and those
  // lanes still have a reorder UI.
  const programItems = data.program ?? [];
  const sortedSpeakers = [...speakers].sort(
    (a, b) => a.sortIndex - b.sortIndex,
  );

  // The desired agenda list depends on per-SECTION bake options
  // (groupProgramByDay + excludedProgrammeIds props) — built lazily per
  // option combo and cached, so multi-section pages sharing the same
  // options build each variant once.
  const agendaVariants = new Map<string, DesiredChild[]>();
  const desiredAgendaFor = (
    grouped: boolean,
    excludedProgrammeIds: readonly string[],
    includedProgramItemIds: readonly string[] | null,
  ): DesiredChild[] => {
    const key = `${grouped ? "day" : "flat"}|${[...excludedProgrammeIds]
      .sort()
      .join(",")}|${
      includedProgramItemIds ? [...includedProgramItemIds].sort().join(",") : "*"
    }`;
    let variant = agendaVariants.get(key);
    if (!variant) {
      variant = buildDesiredAgendaChildren(
        programItems,
        speakersById,
        data.timeZone,
        grouped,
        data.locale,
        excludedProgrammeIds,
        includedProgramItemIds ?? undefined,
      );
      agendaVariants.set(key, variant);
    }
    return variant;
  };
  // Speaker sets are PER SECTION for the same reason agenda variants are:
  // two Speaker sections on one page may carry different picks, and a single
  // globally-built list would give both of them the same children.
  const speakerVariants = new Map<string, DesiredChild[]>();
  const desiredSpeakersFor = (
    includedSpeakerIds: readonly string[] | null,
  ): DesiredChild[] => {
    const key = includedSpeakerIds
      ? [...includedSpeakerIds].sort().join(",")
      : "*";
    let variant = speakerVariants.get(key);
    if (!variant) {
      variant = selectSpeakersForSection(sortedSpeakers, {
        includedSpeakerIds: includedSpeakerIds ?? undefined,
      }).map((s) => ({
        entityId: s.speakerId as string,
        props: mapSpeakerToSpeakerChildProps(s, data.speakerCustomDefs),
      }));
      speakerVariants.set(key, variant);
    }
    return variant;
  };

  const sortedSponsors = [...(data.sponsors ?? [])]
    .sort((a, b) => a.sortIndex - b.sortIndex)
    .filter((s) => Boolean(s.sponsorId));

  // T-wave configurable tiers: undefined = unavailable (leave tier props
  // untouched), [] = no config (legacy path), non-empty = dynamic bands.
  const canTiers = data.sponsorTiers !== undefined;
  const sortedTiers = [...(data.sponsorTiers ?? [])].sort(
    (a, b) =>
      a.sortIndex - b.sortIndex ||
      (a.createdAt ?? "").localeCompare(b.createdAt ?? ""),
  );
  const tierConfigActive = canTiers && sortedTiers.length > 0;
  const legacyBandByTierId = tierConfigActive
    ? computeLegacyBandForTiers(sortedTiers)
    : null;
  // tier/tierId are reconciled on BOTH paths (audit D1) — the kind's
  // CREATE_ONLY_PROPS set is already {isVisible}, no override needed.

  let changed = 0;
  let nodes: Record<string, NodeLike | undefined>;
  try {
    nodes = query.getNodes();
  } catch {
    return 0;
  }

  const childNameOf = (node: NodeLike | undefined) =>
    SECTION_CHILD_DISPLAY_NAME[node?.data.displayName ?? ""];

  /** Project a still-bound section onto "bound" and record the mode. */
  const projectBound = (
    nodeId: string,
    node: NodeLike | undefined,
    props: Record<string, unknown>,
  ): number => {
    let n = applySectionBindingVisibility(
      nodeId,
      "bound",
      { childDisplayName: childNameOf(node) },
      query,
      actions,
    );
    if (props[BINDING_VISIBILITY_PROP] !== "bound") {
      actions.setProp(nodeId, (p) => {
        p[BINDING_VISIBILITY_PROP] = "bound";
      });
      n += 1;
    }
    return n;
  };

  for (const [nodeId, node] of Object.entries(nodes)) {
    const props = node?.data.props;
    if (!isRecord(props)) continue;

    // Computed WITHOUT the per-kind availability gates: a section whose
    // entity query failed must fall into neither the bake nor the sweep.
    const boundKind =
      props.programBinding === PROGRAM_BINDING_KEY
        ? "agenda"
        : props.speakersBinding === SPEAKERS_BINDING_KEY
          ? "speaker"
          : props.sponsorsBinding === SPONSORS_BINDING_KEY
            ? "sponsor"
            : null;

    if (!boundKind) {
      // The UNBIND sweep. Fires only for sections this code itself bound
      // (`bindingVisibility` present) — legacy documents have no such prop
      // and are left exactly as they are, including the ones whose generated
      // children were adopted as visible manual content by the old unbind.
      const stamped = props[BINDING_VISIBILITY_PROP];
      if (stamped === "bound" || stamped === "unbound") {
        changed += applySectionBindingVisibility(
          nodeId,
          stamped === "bound" ? "unbound" : "unbound-steady",
          { childDisplayName: childNameOf(node) },
          query,
          actions,
        );
        if (stamped === "bound") {
          actions.setProp(nodeId, (p) => {
            p[BINDING_VISIBILITY_PROP] = "unbound";
          });
          changed += 1;
        }
      }
      continue;
    }

    if (canAgenda && props.programBinding === PROGRAM_BINDING_KEY) {
      // Track exclusion is a PER-SECTION prop like groupProgramByDay —
      // tolerant read (non-arrays/non-strings = no exclusion).
      const excludedProgrammeIds = Array.isArray(props.excludedProgrammeIds)
        ? (props.excludedProgrammeIds as unknown[]).filter(
            (v): v is string => typeof v === "string",
          )
        : [];
      const desiredAgenda = desiredAgendaFor(
        props.groupProgramByDay === true,
        excludedProgrammeIds,
        readIdList(props.includedProgramItemIds),
      );
      changed += syncBoundSection(
        nodeId,
        desiredAgenda,
        "agenda",
        query,
        actions,
        factories,
        data.locale,
      );
      changed += projectBound(nodeId, node, props);
    }
    if (canSpeaker && props.speakersBinding === SPEAKERS_BINDING_KEY) {
      const desiredSpeakers = desiredSpeakersFor(
        readIdList(props.includedSpeakerIds),
      );
      changed += syncBoundSection(
        nodeId,
        desiredSpeakers,
        "speaker",
        query,
        actions,
        factories,
        data.locale,
        data.speakerImagePlaceholder,
      );
      changed += projectBound(nodeId, node, props);
    }
    if (canSponsor && props.sponsorsBinding === SPONSORS_BINDING_KEY) {
      // T-wave: bake the SECTION-level tier props first. With an active
      // config: `tierBands` (preserving user heading overrides) + the
      // legacy heading pairs (single-mapper rule) for old snapshots. With
      // an explicitly EMPTY config ([]): remove a stale tierBands prop so
      // the wall falls back to the legacy path after tiers were deleted.
      if (canTiers) {
        if (tierConfigActive) {
          const desiredBands = buildTierBandsSectionProp(
            sortedTiers,
            props.tierBands,
          );
          const legacyHeadingProps = buildLegacyHeadingProps(desiredBands);
          const desiredSectionProps: Record<string, unknown> = {
            tierBands: desiredBands,
            ...legacyHeadingProps,
          };
          // FIRST config write on this section: stash the user's pre-config
          // legacy heading setup — the config-derived pairs overwrite it,
          // and deleting the config later must RESTORE it (one-way-door fix).
          if (props.tierBands === undefined && props.tierLegacyHeadingBackup === undefined) {
            desiredSectionProps.tierLegacyHeadingBackup = {
              tierGoldEnabled: props.tierGoldEnabled ?? false,
              tierGoldLabel: props.tierGoldLabel ?? null,
              tierSilverEnabled: props.tierSilverEnabled ?? false,
              tierSilverLabel: props.tierSilverLabel ?? null,
              tierBronzeEnabled: props.tierBronzeEnabled ?? false,
              tierBronzeLabel: props.tierBronzeLabel ?? null,
            };
          }
          const sectionDiffers = Object.entries(desiredSectionProps).some(
            ([key, value]) =>
              JSON.stringify(props[key] ?? null) !==
              JSON.stringify(value ?? null),
          );
          if (sectionDiffers) {
            writeProps(
              actions,
              nodeId,
              desiredSectionProps,
              Object.keys(desiredSectionProps),
            );
            changed += 1;
          }
        } else if (props.tierBands !== undefined) {
          actions.setProp(nodeId, (p) => {
            delete p.tierBands;
            // Config deleted → restore the stashed pre-config heading setup
            // (fall back to all-disabled when no backup exists — stale
            // config-derived pairs would mislabel the legacy wall).
            const backup = p.tierLegacyHeadingBackup as
              | Record<string, unknown>
              | undefined;
            p.tierGoldEnabled = (backup?.tierGoldEnabled as boolean) ?? false;
            p.tierGoldLabel = backup?.tierGoldLabel ?? undefined;
            p.tierSilverEnabled = (backup?.tierSilverEnabled as boolean) ?? false;
            p.tierSilverLabel = backup?.tierSilverLabel ?? undefined;
            p.tierBronzeEnabled = (backup?.tierBronzeEnabled as boolean) ?? false;
            p.tierBronzeLabel = backup?.tierBronzeLabel ?? undefined;
            delete p.tierLegacyHeadingBackup;
          });
          changed += 1;
        }
      }
      // Selection filters are PER-SECTION props (ids / tiers / tags) — read
      // them off this node, like groupProgramByDay for agenda sections.
      const desiredSponsors: DesiredChild[] = filterSponsorsForSection(
        sortedSponsors,
        props,
        legacyBandByTierId,
      ).map((s) => ({
        entityId: s.sponsorId as string,
        props: mapSponsorToSponsorChildProps(
          s,
          legacyBandByTierId,
          data.sponsorCustomDefs,
        ),
      }));
      changed += syncBoundSection(
        nodeId,
        desiredSponsors,
        "sponsor",
        query,
        actions,
        factories,
        data.locale,
        undefined,
        undefined,
      );
      changed += projectBound(nodeId, node, props);
    }
  }
  return changed;
}
